import { useState, useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import type { CourseDto, PaginatedCoursesDto, UserCourseProgressDto } from '@prometeus/contracts';

export const Route = createFileRoute('/certificacao/')({
  component: CertificacaoIndexPage,
});

const CERTIFICATION_FROM_WORKSPACE_KEY = 'certificationFromWorkspaceId';

function CertificacaoIndexPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  useEffect(() => {
    try {
      sessionStorage.removeItem(CERTIFICATION_FROM_WORKSPACE_KEY);
    } catch {}
  }, []);
  const limit = 9;

  const { data, isLoading } = useQuery({
    queryKey: ['certification-courses', page],
    queryFn: async (): Promise<PaginatedCoursesDto> => {
      const response = await api.get('/certification/courses', { params: { page, limit } });
      return response.data?.data ?? response.data;
    },
  });

  const courses = data?.data ?? [];
  const totalPages = data?.totalPages ?? 1;
  const total = data?.total ?? 0;

  return (
    <div className="p-6 max-w-6xl mx-auto" style={{ fontFamily: 'Poppins, sans-serif' }}>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#06141B] dark:text-white">Certificação</h1>
        <p className="text-[#253745] dark:text-gray-400 mt-1">
          Cursos disponíveis para conclusão e emissão de certificado.
        </p>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="w-full max-w-[351px] rounded-[10px] border border-[#9BA8AB]/30 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-[0_0_15px_rgba(0,0,0,0.05)] animate-pulse h-[295px]" />
          ))}
        </div>
      ) : courses.length === 0 ? (
        <div className="rounded-2xl border border-[#9BA8AB]/30 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg p-12 text-center">
          <p className="text-[#4A5C6A] dark:text-gray-400">Nenhum curso publicado no momento.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course: CourseDto) => (
              <CourseCard
                key={course.id}
                course={course}
                onOpen={() => navigate({ to: '/certificacao/cursos/$courseId', params: { courseId: course.id } })}
              />
            ))}
          </div>
          {totalPages > 1 && (
            <div className="mt-8 flex justify-center items-center gap-3">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-5 py-2.5 rounded-full border border-[#4A5C6A]/40 dark:border-gray-600 bg-white dark:bg-gray-800 text-[#253745] dark:text-gray-300 font-medium shadow-md disabled:opacity-50 focus:outline-none focus:ring-0"
              >
                Anterior
              </button>
              <span className="px-4 py-2 text-[#4A5C6A] dark:text-gray-400 text-sm">
                Página {page} de {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-5 py-2.5 rounded-full border border-[#4A5C6A]/40 dark:border-gray-600 bg-white dark:bg-gray-800 text-[#253745] dark:text-gray-300 font-medium shadow-md disabled:opacity-50 focus:outline-none focus:ring-0"
              >
                Próxima
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CourseCard({
  course,
  onOpen,
}: {
  course: CourseDto;
  onOpen: () => void;
}) {
  const { data: progress } = useQuery<UserCourseProgressDto | null>({
    queryKey: ['certification-progress', course.id],
    queryFn: async () => {
      try {
        const response = await api.get(`/certification/courses/${course.id}/progress`);
        return response.data?.data ?? response.data;
      } catch {
        return null;
      }
    },
    enabled: !!course.id,
  });

  const progressPercent = progress?.progressPercent ?? 0;
  const status = progress?.status ?? 'not_started';
  const hasCertificate = status === 'certified' && (progress?.certificateId ?? null) != null;
  const label =
    status === 'certified'
      ? hasCertificate ? 'Ver certificado' : 'Concluído'
      : status === 'eligible_for_quiz' || status === 'all_steps_done'
        ? 'Simulado'
        : progressPercent > 0
          ? 'Continuar'
          : 'Iniciar certificação';

  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full max-w-[351px] text-left overflow-hidden transition-all focus:outline-none focus:ring-0 hover:opacity-95"
      style={{
        height: 295,
        borderRadius: 10,
        boxShadow: '0 0 15px rgba(0,0,0,0.05)',
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      {/* Seção superior - capa */}
      <div
        className="relative w-full flex items-center justify-center overflow-hidden"
        style={{
          height: 295 - 130,
          backgroundColor: '#06141B',
        }}
      >
        {course.coverUrl ? (
          <img
            src={course.coverUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-60"
          />
        ) : (
          <span className="text-4xl text-white/40">📚</span>
        )}
      </div>

      {/* Seção inferior - barra branca 142px */}
      <div
        className="flex flex-col justify-between px-4 pt-3 pb-6 bg-white dark:bg-gray-800"
        style={{ height: 130 }}
      >
        <div className="flex items-start gap-3">
          <img
            src={course.workspaceImageUrl || '/logo-preta.svg'}
            alt=""
            className={`w-8 h-8 flex-shrink-0 object-cover ${course.workspaceImageUrl ? 'rounded-full' : 'dark:invert'}`}
            onError={(e) => {
              const target = e.currentTarget;
              if (!target.dataset.fallback) {
                target.dataset.fallback = '1';
                target.src = '/logo-preta.svg';
                target.classList.remove('rounded-full');
                target.classList.add('dark:invert');
              }
            }}
          />
          <div className="min-w-0 flex-1">
            <h3
              className="font-semibold line-clamp-2 leading-tight text-[#06141B] dark:text-white"
              style={{ fontSize: 16 }}
            >
              {course.title}
            </h3>
            {course.estimatedHours != null && (
              <p
                className="mt-0.5 text-[#9BA8AB]"
                style={{ fontSize: 12 }}
              >
                {course.estimatedHours} {course.estimatedHours === 1 ? 'hora' : 'horas'}
              </p>
            )}
          </div>
        </div>

        <div className="mt-2">
          <div className="flex items-center justify-between">
            <span className="text-[#9BA8AB]" style={{ fontSize: 12 }}>{label}</span>
            <span className="text-[#9BA8AB]" style={{ fontSize: 12 }}>{progressPercent}%</span>
          </div>
          <div
            className="mt-1 h-1.5 rounded-full overflow-hidden bg-[#E5E7EB] dark:bg-gray-600"
          >
            <div
              className="h-full rounded-full bg-[#06141B] dark:bg-[#11212D] transition-all"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </div>
    </button>
  );
}
